/**
 * Client error tracking (Sentry).
 *
 * The blind spot this closes: someone whose bunker crashes does not fill in the
 * feedback form — they close the tab. Analytics counts their arrival, the
 * feedback button hears the vocal minority, and this is the only sensor that
 * sees what actually broke for everyone else.
 *
 * Errors only. No performance traces, no session replay — that keeps the free
 * tier out of reach and matches the analytics posture in CLAUDE.md.
 *
 * Absent `VITE_SENTRY_DSN`, every export here is inert. That is the normal
 * state in dev and in tests.
 */
import { init, setUser, captureException } from '@sentry/react';

/** Guards init against a double-invoke (StrictMode, a re-render, a re-import). */
let initialised = false;

/** Inert unless a DSN was baked into the bundle at build time. */
function configured(): boolean {
  return Boolean(import.meta.env.VITE_SENTRY_DSN);
}

export function initErrorTracking(): void {
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  if (!dsn || initialised) return;

  init({
    dsn,
    environment: import.meta.env.DEV ? 'development' : 'production',
    // Errors only, deliberately: traces and replay are volume, and replay is
    // ruled out by the no-session-replay decision in CLAUDE.md.
    tracesSampleRate: 0,
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 0,
    // No IP, no headers, no cookies. The one identifier we attach is the
    // account UUID, set explicitly in setErrorUser.
    sendDefaultPii: false,
  });
  initialised = true;
}

/**
 * Attach the account id to subsequent events, or clear it on sign-out.
 *
 * The UUID only. An email would let us mail the person whose bunker broke, but
 * it would also put personal data in a third party — a call the founder made
 * deliberately (2026-08-27). Never widen this without revisiting that.
 */
export function setErrorUser(userId: string | null): void {
  if (!configured()) return;
  setUser(userId ? { id: userId } : null);
}

/** Report a caught error. Safe to call from a catch block when inert. */
export function reportError(err: unknown): void {
  if (!configured()) return;
  captureException(err);
}
